import * as signalR from '@microsoft/signalr';
import { notifySubscriptionsOfDisconnect, notifySubscriptionsOfReconnected } from './signalrSubscriptionHandler';

let connection: null | signalR.HubConnection;

let firstReconnectTime: Date | null = null;

let connectingPromise: null | Promise<void> = null;

const connectSignalr = async () => {
    // If we are already connecting, we dont need to start connecting again, just wait for the connection to complete
    if (connectingPromise) {
        await connectingPromise;
        return;
    }

    console.log('Starting to connect to signalr');

    connection = new signalR.HubConnectionBuilder()
        .withUrl('/signalr')
        .configureLogging(signalR.LogLevel.Information)
        .withAutomaticReconnect({
            nextRetryDelayInMilliseconds: (retryContext) => {
                // We keep retrying infinitely with a random delay between 0 and 10 seconds
                const retryMs = Math.random() * 10000;
                console.warn(`ManagerHub is disconnected. Trying to retry after ${retryMs}ms. Retry reason:`, retryContext.retryReason);
                return retryMs;
            },
        })
        .build();

    connectingPromise = new Promise((resolveFullyConnectedPromise, rejectFullyConnected) => {
        if (!connection) {
            rejectFullyConnected(new Error('Connection is null. This should be unreachable'));
            return;
        }

        connection
            .start()
            .then(() => {
                console.log('signalr successfully connected');
                resolveFullyConnectedPromise();
            })
            .catch((error) => {
                console.error('Could not connect to signalr: ', error);
                // TODO: This is not properly handled by the rest of the code, but optimally we should always succeed at connecting
                // This will bubble the error all the way to the caller of connectSignalr
                rejectFullyConnected(error);
            });
    });
    await connectingPromise;

    // This can be used to manually disconnect from signalr, so we can simulate a disconnect
    // This is left here so I dont have to find out how to do this the next time we need to debug a disconnect thing
    // window['disconnectSignalr'] = () => {
    //     connection.connection.transport.stop();
    // };

    /*
     * This is triggered when the connection is closed either by connection.stop() or if we eventually quit the reconnecting
     * TODO: Should probably show a toast message here to notify the user that the connection has been lost and we need to refresh the page
     */
    connection.onclose(async (error) => {
        console.error('The connection with the server was closed', error);
    });

    /**
     * Triggered when the connection is reconnecting
     */
    connection.onreconnecting(() => {
        if (!firstReconnectTime) {
            // Tell the subscriptions that we disconnected so they should keep track of what message sequenceNumber they are currently on
            notifySubscriptionsOfDisconnect();

            // Save when we started trying to reconnect so we can eventually give up
            firstReconnectTime = new Date();
            console.warn('The server connection was lost. Trying to reconnect', firstReconnectTime);
        }

        // We dont care about the reconnecting event.
        console.debug('ManagerHub is reconnecting');
    });

    /**
     * Triggered when the connection is reconnected
     * @remarks this causes all the subscriptions to be invalidated so we need to resubscribe. ( because the connection changes id)
     */
    connection.onreconnected(async () => {
        const timeDisconnectedMs = new Date().getTime() - (firstReconnectTime ?? new Date()).getTime();

        console.warn(`The connection was reestablished after ${timeDisconnectedMs / 1000} seconds`);

        firstReconnectTime = null;
        notifySubscriptionsOfReconnected();
    });

    // Wait to see if we are actually connected.
    // TODO: Is this needed?
    let connectionCheckTries = 0;
    const connectionCheckTriesThreshold = 10;
    const connectionCheckInterval = 50;
    /**
     * Wait {@link connectionCheckInterval} ms to see if we have successfully connected
     */
    await new Promise<void>((resolveConnectionAfterMsCheck) => {
        const interval = setInterval(() => {
            if (connection?.state == signalR.HubConnectionState.Connected) {
                clearInterval(interval);
                resolveConnectionAfterMsCheck();
            }
            connectionCheckTries++;
            if (connectionCheckTries > connectionCheckTriesThreshold) {
                clearInterval(interval);
                throw new Error(
                    `Timed out trying to connect to signalr after ${connectionCheckTriesThreshold * connectionCheckInterval}ms`,
                );
            }
        }, connectionCheckInterval);
    });
};

/**
 * Invokes a serverside signalR method
 * @param method The method to invoke on the server
 * @param args The arguments to pass to the server
 * @returns void
 */
export const invokeServerSideMethod = async <T>(method: string, ...args: any[]): Promise<T> => {
    try {
        if (connection?.state != signalR.HubConnectionState.Connected) {
            await connectSignalr();
        }
        return connection!.invoke(method, ...args);
    } catch (e) {
        console.error('Could not connect to signalr: ', e);
    }
    throw new Error('Could not invoke serverside method' + method);
};

/**
 * Listens to messages from the server
 * This should NOT be used manually, it will be used by the autogenerated signalR code
 * Use the autogenerated code instead
 * @param key The key to listen to
 * @param callback The callback funtion to be called when we receive data
 * @returns void
 */
export const registerSignalrCallback = async (key: string, callback: (...args: any[]) => void) => {
    try {
        if (connection?.state != signalR.HubConnectionState.Connected) {
            await connectSignalr();
        }

        connection!.on(key, callback);
    } catch (e) {
        console.error('Signalr connection not alive: ', e);
    }
};

/**
 * Deregsiters a callback from a key
 * This should NOT be used manually, it will be used by the autogenerated signalR code
 * Use the autogenerated code instead
 * The callback needs to be the same instance as the one used to register the key!
 * @param key The key to deregister
 * @param callback The callback used to register the key in the first place. This NEEDS to be the same callback instance!
 * @returns
 */
export const deregisterSignalrCallback = async (key: string, callback: (...args: any[]) => void) => {
    // If we arent connected then we cant deregister (one would assume)
    if (connection?.state != signalR.HubConnectionState.Connected) {
        return;
    }
    try {
        connection!.off(key, callback);
    } catch (e) {
        console.error('Signalr connection not alive: ', e);
    }
};
